(ns client-portal.client.smart-ordering.sessions.data
  (:require [shared.core.reactive :as re]
            [client-portal.client.concepts.data :as concepts.d]
            [client-portal.client.core.data :as core.d]
            [client-portal.client.router :as cp.router]
            [client-portal.client.smart-ordering.filters.data]
            [client-portal.client.voice-insights.data :as vi.d]
            [client-portal.client.voice-insights.sessions.data :as vi.sessions.d]
            [shared.core.router :as router]
            [shared.core.time :as time]
            [shared.core.util :as util]
            [shared.core.utils.currency :as currency]))

(def root-path [:smart-ordering :sessions])
(def list-path (conj root-path :list))
(def count-path (conj root-path :count))
(def selected-conversation-path (conj root-path :selected-conversation))
(def utterances-list-path (conj root-path :utterances :list))
(def header-height-path (conj root-path :header-height))
(def table-columns-path (conj root-path :table-columns))
(def filters-config {:root-path root-path
                     :reload-event [:smart-ordering/reload-sessions]})
;; The Voice Insights transcript list (reused in the panel) hides its phase highlights
;; while this path holds a search.
(def search-transcript-path (conj vi.sessions.d/root-path :search-transcript))
(def page-size 30)
(def panel-key :smart-ordering/session-panel)
(def sessions-table-hidden-conversation-tag-names #{"outcome" "ai-outcome" "environment"})
(def sessions-table-hidden-utterance-tag-names #{"phase" "confirmation"})

(defn tag-by-name [tags tag-name]
  (some #(when (= (:name %) tag-name) %) tags))

(defn tag-value [tags tag-name]
  (:value (tag-by-name tags tag-name)))

(defn tags-by-name [tags tag-name]
  (filterv #(= (:name %) tag-name) tags))

(defn child-value [tag child-name]
  (some #(when (= (:name %) child-name) (:value %)) (:children tag)))

(defn minor-units->price [v]
  (some-> v (util/parse-int) (currency/currency-int->readable-price)))

(defn order-price [tags tag-name]
  (minor-units->price (tag-value tags tag-name)))

(defn final-items [tags]
  (mapv (fn [t]
          {:name (:value t)
           :price (minor-units->price (child-value t "price"))
           :quantity (or (util/parse-int (child-value t "quantity")) 1)})
        (tags-by-name tags "internal/final-item")))

(defn item-count [items]
  (reduce + 0 (map :quantity items)))

(defn upsell-value [tags]
  (some-> (tag-by-name tags "add-on-upsell")
          (child-value "internal/value")
          (util/parse-int)
          (#(when (pos? %) %))
          (minor-units->price)))

(defn base-query-args
  "Voice Insights' date/concept/location args, minus what smart ordering doesn't have:
   a default namespace, crew types, the live dataset and the most-recent timeframe."
  [db args]
  (let [timeframe (core.d/selected-timeframe db)
        namespace (-> db router/current-query-params :ns not-empty)]
    (-> (vi.d/base-query-args db (cond-> args
                                         (contains? #{core.d/most-recent-timeframe-value -2} timeframe)
                                         (assoc :timeframe core.d/default-timeframe-value)))
        (dissoc :namespace :crew-type :dataset :timeframe)
        (cond-> namespace (assoc :namespace namespace)))))

(defn page-path [page]
  (conj list-path page))

(defn current-max-page [db]
  (some->> (get-in db list-path)
           (util/when-not-loading)
           (keys)
           (apply max)))

(defn list-loading? [db]
  (let [list-data (get-in db list-path)]
    (or (util/loading? list-data) (some-> list-data (get 0) (util/loading?)))))

(re/reg-sub :smart-ordering/sessions-list-loading?- list-loading?)

(defn next-page-loading? [db]
  (let [page (current-max-page db)]
    (and (pos? page) (util/loading? (get-in db (page-path page))))))

(re/reg-sub :smart-ordering/sessions-next-page-loading?- next-page-loading?)

(defn has-more? [db]
  (some->> (get (util/when-not-loading (get-in db list-path)) (current-max-page db))
           (util/when-not-loading)
           (count)
           (<= page-size)))

(re/reg-sub :smart-ordering/sessions-has-more?- has-more?)

(defn next-page [db]
  (some-> (current-max-page db) (inc)))

(re/reg-sub :smart-ordering/sessions-next-page- next-page)

(re/reg-auth0-loader
  :smart-ordering/load-sessions
  (fn [[[_ {:keys [page]}]] _]
    (page-path (or page 0)))
  (fn [db [_ {:keys [page]} _]]
    (when (and (not (next-page-loading? db))
               (not (list-loading? db))
               (concepts.d/current-concept-id db))
      (let [last-loaded (some-> (util/when-not-loading (get-in db list-path))
                                (get (or (dec (or page 0)) 0))
                                (last))]
        {:method :post
         :url "/api/smart-ordering/conversations/list"
         :json-params (merge (base-query-args db {:end-date (:conversation-ts-raw last-loaded)
                                                  :limit page-size})
                             {:end-date-conversation-id (:conversation-id last-loaded)})})))
  :transf (fn [resp]
            (or (some->> resp
                         :body
                         (util/->kebab-case-keywords)
                         (mapv #(assoc %
                                  :conversation-ts-raw (:conversation-ts %)
                                  :conversation-ts (time/reformat-date-str (:conversation-ts %) "M/d/yy h:mm A"))))
                []))
  :error-transf (fn [] []))

(re/reg-sub
  :smart-ordering/sessions-list-
  (fn [db]
    (let [current-list (get-in db list-path)]
      (if (nil? current-list)
        (re/dispatch-load [:smart-ordering/load-sessions [nil {:page 0}]])
        current-list))))

(defn flattened-list [list-data]
  (some->> (util/when-not-loading list-data)
           (into (sorted-map))
           (remove #(util/loading? (last %)))
           (mapcat last)
           (vec)))

(re/reg-sub
  :smart-ordering/sessions-flattened-
  :<- [:smart-ordering/sessions-list-]
  flattened-list)

(re/reg-auth0-loader
  :smart-ordering/load-sessions-count
  count-path
  (fn [db]
    (when (concepts.d/current-concept-id db)
      {:method :post
       :url "/api/smart-ordering/conversations/count"
       :json-params (base-query-args db {})}))
  :transf #(-> % :body (util/->kebab-case-keywords) :count)
  :error-transf (fn [] 0))

(re/reg-auth0-load-sub
  :smart-ordering/sessions-count-
  count-path
  :smart-ordering/load-sessions-count)

(re/reg-event-db
  :smart-ordering/reload-sessions re/middleware
  (fn [db]
    (-> db
        (assoc-in list-path nil)
        (assoc-in utterances-list-path nil)
        (assoc-in count-path nil))))

(defn conversation-path [{:keys [conversation-id]}]
  (conj root-path :conversation-by-id conversation-id))

(re/reg-auth0-loader
  :smart-ordering/load-conversation
  (fn [[[_ args]]]
    (conversation-path args))
  (fn [db [_ {:keys [conversation-id dataset-id]}]]
    (when conversation-id
      {:method :get
       :url "/api/smart-ordering/conversation"
       :query-params (util/remove-nil-from-map {:conversation-id conversation-id
                                                :concept-id (concepts.d/current-concept-id db)
                                                :dataset-id dataset-id})}))
  :transf #(or (some-> % :body (util/->kebab-case-keywords)) [])
  :error-transf (fn [] []))

(re/reg-auth0-load-sub
  :smart-ordering/conversation-
  (fn [_ args]
    (conversation-path args))
  :smart-ordering/load-conversation)

(re/reg-sub
  :smart-ordering/selected-session-
  (fn [db]
    (get-in db selected-conversation-path)))

(defn go-with-merged-conv [conv]
  (re/dispatch [:router/go-with-merged-query-params {:conv (vi.sessions.d/conversation->conv-qp conv)}]))

(re/reg-event-db
  :smart-ordering/toggle-session-panel re/middleware
  (fn [db [args]]
    (let [currently-open? (= (get-in db [:layout :active-panel-right 0]) panel-key)
          open? (:open? args true)
          cur-convo (get-in db selected-conversation-path)
          args (cond-> (dissoc args :open?)
                       (and (= (:conversation-id cur-convo) (:conversation-id args))
                            (:convo-idx cur-convo)
                            (not (:convo-idx args)))
                       (assoc :convo-idx (:convo-idx cur-convo)))]
      (cond
        (not open?) (re/dispatch [:layout/hide-panel-right])
        (not currently-open?) (re/dispatch [:layout/show-panel-right [panel-key]]))
      (go-with-merged-conv (when open? args))
      (assoc-in db selected-conversation-path (when open? args)))))

(re/reg-event-db
  :smart-ordering/move-session re/middleware
  (fn [db [direction]]
    (let [sessions (flattened-list (get-in db list-path))
          idx (get-in db (conj selected-conversation-path :convo-idx))
          next-idx (case direction
                     :next (when (< idx (dec (count sessions))) (inc idx))
                     :prev (when (pos? idx) (dec idx)))
          next-session (some->> next-idx (get sessions))]
      (when (and (= direction :next) next-idx (>= next-idx (dec (count sessions))))
        (re/dispatch [:smart-ordering/load-sessions [nil {:page (next-page db)}]]))
      (if next-session
        (let [conv (-> next-session
                       (select-keys [:conversation-id :dataset-id :conversation-ts])
                       (assoc :convo-idx next-idx))]
          (go-with-merged-conv (dissoc conv :convo-idx))
          (assoc-in db selected-conversation-path conv))
        db))))

(defn sharable-session-url [concept-id conversation]
  (str js/window.location.origin
       (cp.router/route-details->href :so/sessions
                                      nil
                                      {:cid concept-id
                                       :conv (vi.sessions.d/conversation->conv-qp
                                               (dissoc conversation :convo-idx))})))

(re/reg-event-fx
  :smart-ordering/sessions-initialize re/middleware
  (fn [{:keys [db]}]
    (re/dispatch [:core/initialize-filters])
    (re/dispatch [:so-filters/initialize filters-config])
    (re/dispatch [:smart-ordering/reload-sessions])
    {:db (assoc-in db selected-conversation-path nil)
     :forward-events {:register :smart-ordering-sessions-initialization-listener
                      :events #{:concepts/select :locations/select :core/select-timeframe
                                :locations/clear-selected :core/timeframe-date-range-changed}
                      :dispatch-to [:smart-ordering/reload-sessions]}}))

(re/reg-sub
  :smart-ordering/view-mode-
  (fn [db]
    (or (:svm (router/current-query-params db)) "sessions")))

(re/reg-event-db
  :smart-ordering/select-view-mode re/middleware
  (fn [db [mode]]
    (re/dispatch [:router/go-with-merged-query-params {:svm mode}])
    db))

(re/reg-sub
  :smart-ordering/header-height-
  (fn [db]
    (get-in db header-height-path)))

(re/reg-event-db
  :smart-ordering/set-header-height re/middleware
  (fn [db [h]]
    (assoc-in db header-height-path h)))
